import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown, ChevronUp, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { VideoPlayer } from "@/components/journey/VideoPlayer";
import { supabase } from "@/integrations/supabase/client";

interface SectionExplainerVideoProps {
  videoUrl: string;
  sectionKey: string; // Used to persist dismissed/minimized state
  title?: string;
  description?: string;
}

/**
 * SectionExplainerVideo - A collapsible video component for section pages
 *
 * - Persists minimized state to localStorage
 * - Persists watch progress to backend (content_usage_analytics)
 * - Resumes progress when user returns
 * - Auto-minimizes after completion for Governance + Workflows pages
 */
export function SectionExplainerVideo({
  videoUrl,
  sectionKey,
  title = "Section Overview",
  description,
}: SectionExplainerVideoProps) {
  const storageKey = `section-video-minimized-${sectionKey}`;
  const requiredWatchPercent = 95;
  const shouldAutoMinimize =
    sectionKey === "governance" ||
    sectionKey === "workflows" ||
    sectionKey === "support" ||
    sectionKey === "task-center";

  const [userId, setUserId] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(() => {
    return localStorage.getItem(storageKey) === "true";
  });
  const [watchProgress, setWatchProgress] = useState(0);

  const initialProgressRef = useRef(0);
  const progressRef = useRef(0);
  const lastSavedIntervalRef = useRef(0);

  // Persist minimized state
  useEffect(() => {
    localStorage.setItem(storageKey, isMinimized.toString());
  }, [isMinimized, storageKey]);

  // Load latest progress from backend (so progress survives navigation/logout/device)
  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        const user = auth.user;
        if (!user) return;

        if (!isMounted) return;
        setUserId(user.id);

        const { data: latest } = await supabase
          .from("content_usage_analytics")
          .select("action_type, metadata, created_at")
          .eq("user_id", user.id)
          .eq("content_type", "section_explainer_video")
          .eq("content_id", sectionKey)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        const meta = (latest?.metadata as any) || {};
        const loadedPercentRaw =
          latest?.action_type === "complete" ? 100 : Number(meta.watch_percent ?? 0);
        const loadedPercent = Number.isFinite(loadedPercentRaw)
          ? Math.max(0, Math.min(100, loadedPercentRaw))
          : 0;

        if (!isMounted) return;

        initialProgressRef.current = loadedPercent;
        progressRef.current = loadedPercent;
        lastSavedIntervalRef.current = Math.floor(loadedPercent / 5);
        setWatchProgress(loadedPercent);

        // If already complete, auto-minimize for the requested pages
        if (shouldAutoMinimize && loadedPercent >= requiredWatchPercent) {
          setIsMinimized(true);
        }
      } catch (error) {
        console.error("Error loading explainer video progress:", error);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [sectionKey, shouldAutoMinimize]);

  // Persist progress periodically (every 5%)
  const saveProgress = useCallback(
    async (percent: number) => {
      if (!userId) return;
      try {
        await supabase.from("content_usage_analytics").insert({
          user_id: userId,
          content_type: "section_explainer_video",
          content_id: sectionKey,
          action_type: "progress",
          metadata: { watch_percent: percent },
        });
      } catch (error) {
        console.error("Error saving video progress:", error);
      }
    },
    [userId, sectionKey]
  );

  const handleProgress = useCallback(
    async (percent: number) => {
      progressRef.current = percent;
      setWatchProgress(percent);

      const currentInterval = Math.floor(percent / 5);
      if (currentInterval > lastSavedIntervalRef.current) {
        lastSavedIntervalRef.current = currentInterval;
        await saveProgress(percent);
      }
    },
    [saveProgress]
  );

  const handleComplete = useCallback(async () => {
    // Mark complete in backend (so resume always comes back as complete)
    if (userId) {
      try {
        await supabase.from("content_usage_analytics").insert({
          user_id: userId,
          content_type: "section_explainer_video",
          content_id: sectionKey,
          action_type: "complete",
          metadata: { watch_percent: 100 },
        });
      } catch (error) {
        console.error("Error saving video completion:", error);
      }
    }

    // Auto-minimize after completion (requested for these pages)
    if (shouldAutoMinimize) {
      setIsMinimized(true);
    }
  }, [userId, sectionKey, shouldAutoMinimize]);

  // Save progress on unmount/navigation away
  useEffect(() => {
    const userIdCopy = userId;
    const sectionKeyCopy = sectionKey;
    
    return () => {
      const current = progressRef.current;
      const initial = initialProgressRef.current;
      if (!userIdCopy) return;
      if (current <= 0 || current <= initial) return;

      // Fire-and-forget save using .then() instead of await
      supabase
        .from("content_usage_analytics")
        .insert({
          user_id: userIdCopy,
          content_type: "section_explainer_video",
          content_id: sectionKeyCopy,
          action_type: "progress",
          metadata: { watch_percent: current },
        })
        .then(({ error }) => {
          if (error) {
            console.error("Error saving progress on unmount:", error);
          }
        });
    };
  }, [userId, sectionKey]);

  if (isMinimized) {
    return (
      <Card className="p-3 mb-6 bg-muted/50 border-dashed">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Play className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">{title}</p>
              <p className="text-xs text-muted-foreground">Click to watch the explainer video</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMinimized(false)}
            className="gap-1"
          >
            <ChevronDown className="h-4 w-4" />
            Expand
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-6 overflow-hidden">
      <div className="p-4 border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">{title}</h3>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMinimized(true)}
            className="gap-1"
          >
            <ChevronUp className="h-4 w-4" />
            Minimize
          </Button>
        </div>
      </div>
      <div className="p-4">
        <VideoPlayer
          videoUrl={videoUrl}
          requiredWatchPercent={requiredWatchPercent}
          onProgress={handleProgress}
          onComplete={handleComplete}
          initialProgress={watchProgress}
        />
      </div>
    </Card>
  );
}
